'use client';
import React, { useMemo } from 'react';
import { Banner, Button, Status, Table } from '@yaradigitallabs/ahua-react';
import type {
  Material,
  MaterialComponent,
  MaterialRouting,
  MaterialWithSoftTag,
  SelectedItemType,
  SoftTag,
} from '../../utils/match-maker.types';
import { LoaderPlaceholder } from '@/components/Placeholder';
import Drawer from '@/components/Drawer';
import { MatchedSoftTagList } from './matched-soft-tags';
import { SoftTagDetailCard } from '../../components/SoftTagDetailCard';
import { useProductionAndConsumptionFilterContext } from '../context';
import { getItemTypeFromSelectedItem } from '../../utils/match-maker.helpers';

type ProductionConsumptionTableProps = {
  material?: Material;
  isLoading?: boolean;
  error?: string | null;
  onClickSelect?: (
    item: Material | MaterialComponent | MaterialRouting | undefined
  ) => void;
};
type MaterialSelectedType = {
  material?: Material;
  component?: MaterialComponent;
  routing?: MaterialRouting;
};

const ProductionConsumptionTable: React.FC<ProductionConsumptionTableProps> = ({
  material,
  isLoading = false,
  error = null,
  onClickSelect = () => {},
}) => {
  const [materialSelected, setMaterialSelected] =
    React.useState<MaterialSelectedType | null>(null);
  const [selectedMaterialTag, setSelectedMaterialTag] =
    React.useState<MaterialWithSoftTag | null>(null);
  const { debouncedTextFilter, unmatchedOnly } =
    useProductionAndConsumptionFilterContext();

  const componentList = useMemo(() => {
    const components = (material?.components || []).filter(component => {
      if (unmatchedOnly) {
        return !component.tags || component.tags.length === 0;
      }
      return true;
    });

    return components.filter(component =>
      component.description?.toLowerCase().includes(debouncedTextFilter)
    );
  }, [material, debouncedTextFilter, unmatchedOnly]);

  const routingList = useMemo(() => {
    const routings = (material?.routings || []).filter(routing => {
      if (unmatchedOnly) {
        return !routing.tags || routing.tags.length === 0;
      }
      return true;
    });

    return routings.filter(routing =>
      routing.object_id?.toLowerCase().includes(debouncedTextFilter)
    );
  }, [material, debouncedTextFilter, unmatchedOnly]);

  const handleMaterialSelect = (
    item: Material | MaterialComponent | MaterialRouting,
    selectedType: SelectedItemType
  ) => {
    setMaterialSelected({
      material: selectedType === 'material' ? (item as Material) : undefined,
      component:
        selectedType === 'component' ? (item as MaterialComponent) : undefined,
      routing:
        selectedType === 'routing' ? (item as MaterialRouting) : undefined,
    });
  };

  const handleMaterialClose = () => {
    setSelectedMaterialTag(null);
    setMaterialSelected(null);
  };

  const handleSoftTagDetailClose = () => {
    setSelectedMaterialTag(null);
  };

  return (
    <>
      <div className="px-4 h-[calc(100vh-320px)] md:h-[calc(100vh-278px)] overflow-auto">
        <Table className="w-full">
          <thead className="sticky top-0 bg-white z-10 shadow-sm">
            <Table.Row>
              <Table.Head className="py-5! px-3! text-nowrap">
                Material number
              </Table.Head>
              <Table.Head className="py-5! px-3! text-nowrap">
                Material description
              </Table.Head>
              <Table.Head className="py-5! px-3! text-nowrap">Type</Table.Head>
              <Table.Head className="py-5! px-3! text-nowrap">
                Status
              </Table.Head>
              <Table.Head className="py-5! px-3! text-nowrap">
                Number of matched soft tags
              </Table.Head>
              <Table.Head className="py-5! px-3!"></Table.Head>
            </Table.Row>
          </thead>
          {isLoading && (
            <tbody>
              {Array.from({ length: 3 }).map((_, index) => (
                <Table.Row className="border-b-0!" key={`loader_${index}`}>
                  <Table.Cell className="py-5! px-3! text-nowrap">
                    <LoaderPlaceholder />
                  </Table.Cell>
                  <Table.Cell className="py-5! px-3! text-nowrap">
                    <LoaderPlaceholder />
                  </Table.Cell>
                  <Table.Cell className="py-5! px-3! text-nowrap">
                    <LoaderPlaceholder />
                  </Table.Cell>
                  <Table.Cell className="py-5! px-3! text-nowrap">
                    <LoaderPlaceholder />
                  </Table.Cell>
                  <Table.Cell className="py-5! px-3! text-nowrap">
                    <LoaderPlaceholder />
                  </Table.Cell>
                  <Table.Cell className="py-5! px-3! text-nowrap">
                    <LoaderPlaceholder />
                  </Table.Cell>
                </Table.Row>
              ))}
            </tbody>
          )}
          {error && (
            <tbody>
              <Table.Row className="border-b-0!">
                <Table.Cell colSpan={6} className="text-center py-10!">
                  <Banner colorConcept="destructiveLight" open>
                    <Banner.Middle>
                      <Banner.Body>
                        <Banner.Message size="n">
                          An error occurred while fetching produced material.
                        </Banner.Message>
                      </Banner.Body>
                    </Banner.Middle>
                  </Banner>
                </Table.Cell>
              </Table.Row>
            </tbody>
          )}
          {!isLoading && !error && !material && (
            <tbody>
              <Table.Row className="border-b-0!">
                <Table.Cell colSpan={6} className="text-center py-10!">
                  No data available
                </Table.Cell>
              </Table.Row>
            </tbody>
          )}
          {!isLoading && !error && material && (
            <tbody>
              <Table.Row>
                <Table.Cell
                  colSpan={6}
                  className="bg-[#F3F6F9] leading-none! py-4! px-3! font-bold"
                >
                  Production
                </Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell className="leading-none! py-4! px-3!">
                  {material?.material_code}
                </Table.Cell>
                <Table.Cell className="leading-none! py-4! px-3!">
                  {material?.description}
                </Table.Cell>
                <Table.Cell className="leading-none! py-4! px-3!">
                  {getItemTypeFromSelectedItem(material)}
                </Table.Cell>
                <Table.Cell className="leading-none! py-4! px-3!">
                  <MaterialStatusDisplay tags={material?.tags} />
                </Table.Cell>
                <Table.Cell className="leading-none! py-4! px-3!">
                  {material?.tags?.length || '-'}
                </Table.Cell>
                <Table.ActionsCell>
                  {material?.tags?.length > 0 ? (
                    <div className="flex justify-end items-center">
                      <Button
                        colorConcept="brand"
                        size="s"
                        variant="ghost"
                        onClick={() =>
                          handleMaterialSelect(material, 'material')
                        }
                      >
                        View matched soft tags
                      </Button>
                    </div>
                  ) : (
                    <div className="flex justify-end items-center">
                      <Button
                        colorConcept="brand"
                        size="s"
                        variant="ghost"
                        iconTrailing="ChevronRight"
                        onClick={() => onClickSelect(material)}
                      >
                        Select
                      </Button>
                    </div>
                  )}
                </Table.ActionsCell>
              </Table.Row>
              <Table.Row>
                <Table.Cell
                  colSpan={6}
                  className="bg-[#F3F6F9] leading-none! py-4! px-3! font-bold"
                >
                  Consumption
                </Table.Cell>
              </Table.Row>
              {componentList.map(component => (
                <Table.Row key={component.id}>
                  <Table.Cell className="leading-none! py-4! px-3!">
                    {component?.component}
                  </Table.Cell>
                  <Table.Cell className="leading-none! py-4! px-3!">
                    {component?.description}
                  </Table.Cell>
                  <Table.Cell className="leading-none! py-4! px-3!">
                    {getItemTypeFromSelectedItem(component)}
                  </Table.Cell>
                  <Table.Cell className="leading-none! py-4! px-3!">
                    <MaterialStatusDisplay tags={component?.tags} />
                  </Table.Cell>
                  <Table.Cell className="leading-none! py-4! px-3!">
                    {component?.tags?.length || '-'}
                  </Table.Cell>
                  <Table.ActionsCell>
                    <div className="flex justify-end items-center">
                      {component?.tags?.length > 0 ? (
                        <Button
                          colorConcept="brand"
                          size="s"
                          variant="ghost"
                          onClick={() =>
                            handleMaterialSelect(component, 'component')
                          }
                        >
                          View matched soft tags
                        </Button>
                      ) : (
                        <Button
                          colorConcept="brand"
                          size="s"
                          variant="ghost"
                          iconTrailing="ChevronRight"
                          onClick={() => onClickSelect(component)}
                        >
                          Select
                        </Button>
                      )}
                    </div>
                  </Table.ActionsCell>
                </Table.Row>
              ))}
              {routingList.map(routing => (
                <Table.Row key={routing?.id}>
                  <Table.Cell className="leading-none! py-4! px-3!">
                    {routing?.workcenter}
                  </Table.Cell>
                  <Table.Cell className="leading-none! py-4! px-3!">
                    {routing?.object_id}
                  </Table.Cell>
                  <Table.Cell className="leading-none! py-4! px-3!">
                    {getItemTypeFromSelectedItem(routing)}
                  </Table.Cell>
                  <Table.Cell className="leading-none! py-4! px-3!">
                    <MaterialStatusDisplay tags={routing?.tags} />
                  </Table.Cell>
                  <Table.Cell className="leading-none! py-4! px-3!">
                    {routing?.tags?.length || '-'}
                  </Table.Cell>
                  <Table.ActionsCell>
                    <div className="flex justify-end items-center">
                      {routing?.tags?.length > 0 ? (
                        <Button
                          colorConcept="brand"
                          size="s"
                          variant="ghost"
                          onClick={() =>
                            handleMaterialSelect(routing, 'routing')
                          }
                        >
                          View matched soft tags
                        </Button>
                      ) : (
                        <Button
                          colorConcept="brand"
                          size="s"
                          variant="ghost"
                          iconTrailing="ChevronRight"
                          onClick={() => onClickSelect(routing)}
                        >
                          Select
                        </Button>
                      )}
                    </div>
                  </Table.ActionsCell>
                </Table.Row>
              ))}
            </tbody>
          )}
        </Table>
      </div>
      <Drawer
        isOpen={!!materialSelected || !!selectedMaterialTag}
        {...(selectedMaterialTag ? { onBack: handleSoftTagDetailClose } : {})}
        onClose={handleMaterialClose}
        title={selectedMaterialTag ? 'Soft tag details' : 'Matched soft tags'}
        position="right"
        widthClass="w-[500px]"
        {...(!!materialSelected && !selectedMaterialTag
          ? {
              footer: (
                <Button
                  css={{ width: '100%' }}
                  colorConcept="brand"
                  size="s"
                  iconLeading="Edit"
                  onClick={() =>
                    onClickSelect(
                      materialSelected.material ||
                        materialSelected.component ||
                        materialSelected.routing
                    )
                  }
                >
                  Edit
                </Button>
              ),
              withSeparator: false,
            }
          : {})}
      >
        {!!materialSelected && !selectedMaterialTag && (
          <MatchedSoftTagList
            list={
              materialSelected.material?.tags ||
              materialSelected.component?.tags ||
              materialSelected.routing?.tags ||
              []
            }
            onSelect={setSelectedMaterialTag}
          />
        )}
        {!!materialSelected && !!selectedMaterialTag && (
          <SoftTagDetailCard
            softTag={
              {
                id: selectedMaterialTag.id,
                asset_name: selectedMaterialTag.tag_name,
                tag_description: selectedMaterialTag.tag_description,
                tag_name: selectedMaterialTag.tag_name,
                site_id: selectedMaterialTag.site_id,
                area: selectedMaterialTag.area,
                production_unit: selectedMaterialTag.production_unit,
                type: selectedMaterialTag.type,
                data_type: selectedMaterialTag.data_type,
                unit: selectedMaterialTag.unit,
                asset_id: selectedMaterialTag.asset_id,
                created_at: selectedMaterialTag.tag_created_at,
                updated_at: '',
              } as SoftTag
            }
          />
        )}
      </Drawer>
    </>
  );
};

const MaterialStatusDisplay: React.FC<{
  tags?: Array<MaterialWithSoftTag>;
}> = ({ tags = [] }) => {
  const isMatched = tags.length > 0;
  return (
    <div className="flex justify-start items-center">
      <Status
        colorType={isMatched ? 'green' : 'gray'}
        iconLeading={isMatched ? 'Check' : 'Skeleton'}
        size="n"
      >
        {isMatched ? 'Matched' : 'Not matched'}
      </Status>
    </div>
  );
};

export default ProductionConsumptionTable;
